import { useMemo } from 'react'
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { Deal } from '../api/deals'
import type { CustomStatus } from '../api/settings'
import { formatCurrency, formatDate, fullName } from '../lib/utils'
import { Avatar } from './ui/Avatar'
import { cn } from '../lib/utils'

interface Props {
  deals: Deal[]
  statuses: CustomStatus[]
  onStageChange: (dealId: string, newStatus: string) => void
}

export function KanbanBoard({ deals, statuses, onStageChange }: Props) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const columns = useMemo(() => {
    return statuses.map(s => ({
      status: s,
      deals: deals.filter(d => d.status === s.label),
    }))
  }, [deals, statuses])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over) return
    const dealId = String(active.id)
    const newStatus = String(over.data.current?.status ?? over.id)
    if (newStatus) onStageChange(dealId, newStatus)
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4 h-full">
        {columns.map(col => (
          <KanbanColumn key={col.status.id} status={col.status} deals={col.deals} />
        ))}
      </div>
      <DragOverlay />
    </DndContext>
  )
}

function KanbanColumn({ status, deals }: { status: CustomStatus; deals: Deal[] }) {
  const { setNodeRef } = useSortable({ id: status.label, data: { status: status.label } })

  return (
    <div
      ref={setNodeRef}
      className="flex-shrink-0 w-64 bg-gray-50 rounded-lg border border-gray-200 flex flex-col"
    >
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
            style={{ backgroundColor: status.color }}
          />
          <span className="text-sm font-medium text-gray-700">{status.label}</span>
        </div>
        <span className="text-xs text-gray-500 bg-white border border-gray-200 rounded px-1.5 py-0.5">
          {deals.length}
        </span>
      </div>
      <SortableContext
        items={deals.map(d => d.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="p-2 space-y-2 flex-1 overflow-y-auto max-h-[calc(100vh-200px)]">
          {deals.map(deal => <DealCard key={deal.id} deal={deal} />)}
        </div>
      </SortableContext>
    </div>
  )
}

function DealCard({ deal }: { deal: Deal }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: deal.id,
    data: { deal },
  })

  const style = { transform: CSS.Transform.toString(transform), transition }

  const assigneeName = deal.assignedUser
    ? fullName(deal.assignedUser.firstName, deal.assignedUser.lastName)
    : null

  const daysInStage = Math.floor((Date.now() - new Date(deal.updatedAt).getTime()) / 86400000)

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'bg-white border border-gray-200 rounded-md p-3 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-shadow',
        isDragging && 'opacity-50',
      )}
    >
      <p className="text-sm font-medium text-gray-900 mb-1.5 line-clamp-2">{deal.title}</p>
      {deal.value != null && (
        <p className="text-sm text-green-700 font-medium mb-1.5">
          {formatCurrency(deal.value, deal.currency)}
        </p>
      )}
      <div className="flex items-center justify-between mt-2">
        {assigneeName ? (
          <Avatar name={assigneeName} size="sm" />
        ) : (
          <span className="w-6 h-6 rounded-full bg-gray-100 border border-dashed border-gray-300" />
        )}
        <span className="text-xs text-gray-400">{daysInStage}d</span>
      </div>
      {deal.expectedCloseDate && (
        <p className="text-xs text-gray-400 mt-1">Close: {formatDate(deal.expectedCloseDate)}</p>
      )}
    </div>
  )
}
